import { LoginResponse, RegisterResponse, UserContainersResponse, UserContainer, CreateContainerResponse, MfaChallengeResponse } from '../models/User';

// Helper to get function URL - accessing process.env safely in browser
const getFunctionUrl = (): string => {
  return process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
};

export class UserAuthService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage if available
    this.token = localStorage.getItem('userAuthToken');
  }

  /**
   * Register a new user
   */
  async register(email: string, password: string, displayName: string): Promise<RegisterResponse> {
    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(`${functionUrl}/register_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          displayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Registration failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: RegisterResponse = await response.json();
      return data;
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Login user and get JWT token
   */
  async login(email: string, password: string): Promise<LoginResponse | MfaChallengeResponse> {
    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(`${functionUrl}/login_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Login failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // If MFA is required, no token is issued yet — the caller must verify the code.
      if (data.mfaRequired) {
        return data as MfaChallengeResponse;
      }

      this.storeToken(data);
      return data as LoginResponse;
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /** Verify an emailed one-time code and complete login. */
  async verifyOtp(challengeId: string, otp: string): Promise<LoginResponse> {
    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(`${functionUrl}/verify_login_otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId, otp }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Verification failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: LoginResponse = await response.json();
      this.storeToken(data);
      return data;
    } catch (error: any) {
      console.error('OTP verification error:', error);
      throw error;
    }
  }

  private storeToken(data: LoginResponse): void {
    if (data.token) {
      this.token = data.token;
      localStorage.setItem('userAuthToken', data.token);
      const expirationTime = Date.now() + (data.expiresIn * 1000);
      localStorage.setItem('userAuthTokenExpiration', expirationTime.toString());
    }
  }

  /**
   * Logout user and clear token
   */
  logout(): void {
    this.token = null;
    localStorage.removeItem('userAuthToken');
    localStorage.removeItem('userAuthTokenExpiration');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    if (!this.token) {
      return false;
    }

    // Check if token has expired
    const expirationTime = localStorage.getItem('userAuthTokenExpiration');
    if (expirationTime && Date.now() > parseInt(expirationTime)) {
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get containers assigned to current user
   */
  async getUserContainers(): Promise<UserContainer[]> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(`${functionUrl}/get_user_containers`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        const errorData = await response.json().catch(() => ({ error: 'Failed to get containers' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: UserContainersResponse = await response.json();
      return data.containers;
    } catch (error: any) {
      console.error('Get containers error:', error);
      throw error;
    }
  }

  /**
   * Create a new Shared Folder (container). Requires the canCreateContainers
   * permission on the user (enforced server-side).
   */
  async createContainer(containerName: string): Promise<CreateContainerResponse> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(`${functionUrl}/create_user_container`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ containerName }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        const errorData = await response.json().catch(() => ({ error: 'Failed to create Shared Folder' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Create container error:', error);
      throw error;
    }
  }

  /**
   * Upload blob to assigned container (authenticated)
   * Note: This will be implemented once the backend function is ready
   */
  async uploadBlobAuthenticated(containerName: string, file: File): Promise<void> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    try {
      const functionUrl = getFunctionUrl();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('containerName', containerName);

      const response = await fetch(`${functionUrl}/upload_blob_authenticated`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired. Please login again.');
        }
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      throw error;
    }
  }
}

export default UserAuthService;

